import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BasicChannel, EncField, EncFieldGroup, Encoding, EncodingResult, VariableType } from '@mode-vegas/vegas';
import { trackByFactory } from '@mode/capra';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, SharedAxisHelpers } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';

interface DndEvent {
  index?: number | undefined;
  event: DragEvent;
}

@Component({
  selector: 'mode-shared-axis-encoding',
  templateUrl: './shared-axis-encoding.component.html',
  styleUrls: ['./shared-axis-encoding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedAxisEncodingComponent implements OnInit, OnChanges {
  @Input() channel!: BasicChannel;
  @Input() isDraggable!: boolean;
  @Input() fieldGroup: EncFieldGroup | undefined;
  @Input() fields!: Array<EncField>;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() format: ChartTypes.ChartFormat | undefined;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;
  @Input() encoding!: Encoding;
  @Input() model!: FlamingoTypes.ModeModel;
  @Input() vizToken = '';
  @Input() queryToken = '';
  @Input() view!: FlamingoTypes.ModeView;
  @Input() dragField!: DragFieldInfo;
  @Input() chartService!: SelectedChartService;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;

  @Output() dragStart = new EventEmitter();
  @Output() dragEnd = new EventEmitter();
  @Output() fieldDragEnd = new EventEmitter();
  @Output() fieldUpdated = new EventEmitter<{ $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }>();
  @Output() remove = new EventEmitter<{ $field: EncField }>();
  @Output() removeSort = new EventEmitter();
  @Output() binEnabled = new EventEmitter<{ field: EncField; token: string; formula: CompiledField }>();
  @Output() fieldBinSizeUpdated = new EventEmitter<{ field: EncField; formula: CompiledField; binSize: number }>();
  @Output() getBinSize = new EventEmitter<{ columnName: string; token: string }>();

  trackById = trackByFactory<EncField>(({ id }) => id);

  groupName = '';
  isHeaderHover = false;
  isDualAxis = false;
  canBeDualAxis = true;
  isDualAxisRelative = true;

  constructor(private chartFacade: ChartFacade) {}

  ngOnInit(): void {
    this.groupName = this.fieldGroup?.name ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isDualAxis = [...this.encoding.y2, ...this.encoding.x2].some((f) => f.groupId === this.fieldGroup?.id);
    this.isDualAxisRelative =
      this.channel === 'row'
        ? [...this.encoding.y2].some((f) => f.y === this.fieldGroup?.id)
        : [...this.encoding.x2].some((f) => f.x === this.fieldGroup?.id);

    const hasEligibleSibling = () => {
      const basicFields =
        this.channel === 'row'
          ? [...this.encoding.row, ...this.encoding.y]
          : [...this.encoding.column, ...this.encoding.x];
      const index = basicFields.findIndex((f) => f.groupId === this.fieldGroup?.id);
      if (index <= 0) {
        return false;
      }
      const prevField = basicFields[index - 1];
      if (prevField.variable.type === VariableType.CONTINUOUS) {
        // Make sure the previous field isn't already part of a dual axis.
        const refId = prevField.groupId ?? prevField.id ?? '';
        const isDualAxisRef =
          this.channel === 'row'
            ? [...this.encoding.y2].some((f) => f.y === refId)
            : [...this.encoding.x2].some((f) => f.x === refId);
        return !isDualAxisRef;
      }
      return false;
    };
    this.canBeDualAxis = this.isDualAxis || (!this.isDualAxisRelative && hasEligibleSibling());
  }

  get hasSingleField() {
    return (this.fields?.length ?? 0) < 2;
  }

  /**
   * This prevents drag-drop highlighting in the group. We are pretty forgiving
   * about what we will allow as long as the field can be represented as continuous.
   * Binned fields are also not allowed.
   * We don't allow Measure Names and a discrete field which can't be aggregated such as;
   *     IF SUM([order_gloss_qty]) > 100 THEN 'GOOD' ELSE 'BAD' END
   */
  get isDisallowedField(): boolean {
    const dragField: DragFieldInfo | undefined = this.dragField;
    if (!dragField) {
      return false;
    }

    if (
      dragField.formula?.isNames ||
      (dragField.formula?.isDiscrete && !dragField.formula?.isAggregateable) ||
      dragField.groupId != null ||
      dragField.formula?.isBinned
    ) {
      return true;
    }
    return false;
  }

  childFieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    this.fieldUpdated.emit($event);
  }

  childFieldBinned($event: { field: EncField; token: string; formula: CompiledField }) {
    this.binEnabled.emit($event);
  }

  childUpdateFieldBinSize($event: { field: EncField; formula: CompiledField; binSize: number }) {
    this.fieldBinSizeUpdated.emit($event);
  }

  removeField($event: { $field: EncField }) {
    this.remove.emit($event);
  }

  onFieldDrop($event: DndEvent, field: EncField) {
    if (this.dragField == null) {
      return;
    }
    const formula = this.dragField.formula;
    this.fieldDragEnd.emit();

    if (!formula || !field.id || !this.fieldGroup) {
      return;
    }
    let newEncoding;

    if (formula?.isField) {
      if (formula.alias === field.id) {
        // inserting field in the same location as it was
        return;
      }
      newEncoding = SharedAxisHelpers.replaceSharedAxisFieldWithFormula(
        this.encoding,
        formula,
        field.id,
        this.fieldGroup.id,
        this.channel
      );
    } else {
      // Remove current field, add new field, add to group
      newEncoding = SharedAxisHelpers.replaceSharedAxisFieldWithNewFormula(
        this.encoding,
        formula,
        field.id,
        this.fieldGroup.id,
        this.channel
      );
    }

    if (newEncoding != null) {
      newEncoding = EncodingHelpers.updateAutomaticMarkType(this.view, this.encoding, newEncoding);
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  onDropItem($event: DndEvent) {
    if (this.dragField == null) {
      return;
    }
    const formula = this.dragField.formula;
    const dragFields = this.dragField.fields;

    this.fieldDragEnd.emit();

    if (!formula || !this.fieldGroup) {
      return;
    }
    // Update encoding
    // This is adding a new field to an existing group
    // 1. Add field to the same channel as the group
    // 2. Set the group id on the new field
    const dropIndex: number | undefined = $event.index;
    let encodingResult: EncodingResult | undefined = undefined;
    if (formula.isField && formula.alias != null) {
      encodingResult = SharedAxisHelpers.moveFormulaToSharedAxis(
        this.encoding,
        formula,
        this.fieldGroup.id,
        this.channel,
        dropIndex,
        dragFields
      );
    } else {
      encodingResult = SharedAxisHelpers.addFormulaToSharedAxis(
        this.encoding,
        formula,
        this.fieldGroup.id,
        this.channel,
        dropIndex
      );
    }
    encodingResult = EncodingHelpers.updateAutomaticMarkType(this.view, this.encoding, encodingResult);
    this.chartService.dispatchEncodingUpdate(encodingResult);
  }

  onDragStart($event: DragEvent, field: EncField) {
    this.dragStart.emit({ event: $event, field, source: this.channel });
  }

  onDragEnd($event: DragEvent, field: EncField) {
    this.dragEnd.emit({ event: $event, field });
  }

  onHeaderMouseEnter() {
    this.isHeaderHover = true;
  }

  onHeaderMouseLeave() {
    this.isHeaderHover = false;
  }

  goToFormat() {
    if (this.fieldGroup) {
      this.chartFacade.sharedAxisEncodingSetGroupToFormat(this.fieldGroup);
    }
  }

  ungroupSharedAxis() {
    if (this.fieldGroup) {
      const encResult = SharedAxisHelpers.ungroupSharedAxis(this.encoding, this.fieldGroup.id);
      this.chartService.dispatchEncodingUpdate(encResult);
    }
  }

  removeEntireGroup() {
    if (this.fieldGroup) {
      const encResult = SharedAxisHelpers.removeSharedAxis(this.encoding, this.fieldGroup.id);
      this.chartService.dispatchEncodingUpdate(encResult);
    }
  }

  toggleDualAxis() {
    if (this.fieldGroup && this.canBeDualAxis) {
      const encResult = SharedAxisHelpers.toggleDualAxisOnSharedAxis(this.encoding, this.fieldGroup.id);
      this.chartService.dispatchEncodingUpdate(encResult);
    }
  }

  onGetBinSize($event: { columnName: string; token: string }) {
    this.getBinSize.emit($event);
  }
}
